import SettingsItemText from "@/components/PageEditor/components/Viewport/components/Sidebar/components/Settings/components/SettingsItemText";
import SettingsSection from "@/components/PageEditor/components/Viewport/components/Sidebar/components/Settings/components/SettingsSection";
import ButtonLinkPopper from "@/components/PageEditor/editor-components/shared/ButtonLinkPopper";
import { ElementDatabaseService, PageService } from "@/services/admin-services";
import { DatabaseElement, Page } from "@/services/admin-services/models";
import { ROOT_NODE, useEditor, useNode } from "@craftjs/core";
import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

const Settings = () => {
    const {
        id,
        actions: { setCustom, setProp },
    } = useNode();
    const { displayName, language, label, href, target, internalElementSlug, internalPageId } = useEditor((state) => ({
        language: state.nodes[ROOT_NODE].data.props.language,
        displayName:
            state.nodes[id] && state.nodes[id].data.custom.displayName
                ? state.nodes[id].data.custom.displayName
                : state.nodes[id].data.displayName,
        label: state.nodes[id] && state.nodes[id].data.props.text,
        href: state.nodes[id] && state.nodes[id].data.props.href,
        target: state.nodes[id] && state.nodes[id].data.props.target,
        internalElementSlug: state.nodes[id] && state.nodes[id].data.props.internalElementSlug,
        internalPageId: state.nodes[id] && state.nodes[id].data.props.internalPageId,
    }));

    const pageService = useMemo(() => new PageService("int"), []);
    const elementService = useMemo(() => new ElementDatabaseService("int", "int-en"), []);

    const [pages, setPages] = useState<Array<Page>>();
    const [availablePages, setAvailablePages] = useState<Array<Page>>([]);
    const [elements, setElements] = useState<Array<DatabaseElement>>([]);

    useEffect(() => {
        pageService.getPages().then(setPages);
        elementService.get().then(setElements);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!pages) return;

        setAvailablePages(pages.filter((p) => p.language === language));
    }, [language, pages]);

    const onEditTarget = (openInNewTab: boolean) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setProp((props: any) => {
            props.target = openInNewTab ? "_blank" : "_self";
        });
    };

    const onEditLink = (href: string, _: string, openInNewTab: boolean, data: Record<string, string>) => {
        if (data["internal-page-id"]) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setProp((props: any) => {
                props.internalPageId = data["internal-page-id"];
                props.internalElementSlug = undefined;
                props.target = openInNewTab ? "_blank" : "_self";
            });
        } else if (data["internal-element-slug"]) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setProp((props: any) => {
                props.internalElementSlug = data["internal-element-slug"];
                props.internalPageId = undefined;
                props.target = openInNewTab ? "_blank" : "_self";
            });
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setProp((props: any) => {
                props.internalElementSlug = undefined;
                props.internalPageId = undefined;
                props.target = openInNewTab ? "_blank" : "_self";
                props.href = href.startsWith("http") || href.startsWith("https") ? href : `https://${href}`;
            });
        }
    };

    const onRemoveLink = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setProp((props: any) => {
            props.href = "";
            props.internalElementSlug = undefined;
            props.internalPageId = undefined;
        });
    };

    return (
        <>
            <SettingsSection
                title="Identification"
                summary={() => {
                    return `${displayName || "-"}`;
                }}
            >
                <Grid size={{ xs: 12 }}>
                    <SettingsItemText
                        type={"text"}
                        value={displayName}
                        onChange={(value: string) => {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            setCustom((custom: any) => (custom.displayName = value));
                        }}
                        fullWidth={true}
                        label="Display name"
                    />
                </Grid>
            </SettingsSection>
            <SettingsSection title="On click action" summary={() => "-"}>
                <Grid size={{ xs: 12 }}>
                    <ButtonLinkPopper
                        active={true}
                        availableElements={elements}
                        availablePages={availablePages}
                        linkProps={{
                            "internal-page-id": internalPageId,
                            "internal-element-slug": internalElementSlug,
                            href,
                            target,
                            label,
                        }}
                        onEdit={onEditLink}
                        onTargetChange={onEditTarget}
                        onRemove={onRemoveLink}
                    />
                </Grid>
            </SettingsSection>
        </>
    );
};

export default Settings;
